import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { useCrm2Dashboard, type DashItem } from './api'
import { DealSheet } from './DealSheet'
import { Crm2Frame, LINK_CHIP, money, shortDay } from './shared'

type Period = 'week' | 'last_week' | 'month' | 'last_month' | 'quarter' | '30d' | '90d'
const PERIODS: Array<[Period, string]> = [
  ['week', 'This week'],
  ['last_week', 'Last week'],
  ['month', 'This month'],
  ['last_month', 'Last month'],
  ['quarter', 'This quarter'],
  ['30d', 'Last 30 days'],
  ['90d', 'Last 90 days'],
]

/** Start and end of a period in the viewer's own calendar, so "this week" is their week. */
function range(p: Period, now: Date): { from: string; to: string } {
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const add = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
  const monday = add(day, -((day.getDay() + 6) % 7))
  let from: Date
  let to: Date
  switch (p) {
    case 'week':
      from = monday
      to = add(day, 1)
      break
    case 'last_week':
      from = add(monday, -7)
      to = monday
      break
    case 'month':
      from = new Date(day.getFullYear(), day.getMonth(), 1)
      to = add(day, 1)
      break
    case 'last_month':
      from = new Date(day.getFullYear(), day.getMonth() - 1, 1)
      to = new Date(day.getFullYear(), day.getMonth(), 1)
      break
    case 'quarter':
      from = new Date(day.getFullYear(), Math.floor(day.getMonth() / 3) * 3, 1)
      to = add(day, 1)
      break
    case '30d':
      from = add(day, -29)
      to = add(day, 1)
      break
    default:
      from = add(day, -89)
      to = add(day, 1)
  }
  return { from: from.toISOString(), to: to.toISOString() }
}

function Change({ now, before }: { now: number; before: number }) {
  if (now === before) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
        <Minus className="size-3.5" />
        same as before
      </span>
    )
  }
  const up = now > before
  const pct = before ? `${Math.round((Math.abs(now - before) / before) * 100)}%` : 'new'
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-xs font-medium', up ? 'text-success' : 'text-danger')}>
      {up ? <ArrowUpRight className="size-3.5" /> : <ArrowDownRight className="size-3.5" />}
      {pct} vs {before} before
    </span>
  )
}

/** How the funnel did in a period against the one before it, and what needs someone today. */
export function DashboardPage() {
  const { workspaceSlug = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const period = (params.get('period') as Period | null) ?? 'month'
  const [now] = useState(() => new Date())
  const { from, to } = range(period, now)
  const q = useCrm2Dashboard(from, to)
  const [drill, setDrill] = useState<string | null>(null)
  const set = (k: string, v: string | null) => {
    const next = new URLSearchParams(params)
    if (v) next.set(k, v)
    else next.delete(k)
    setParams(next, { replace: true })
  }
  const open = (id: string) => set('deal', id)

  return (
    <Crm2Frame
      title="Dashboard"
      description="The funnel for the period you pick, against the same length of time before it. Click any number for the deals behind it."
      actions={
        <select aria-label="Period" value={period} onChange={(e) => set('period', e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
          {PERIODS.map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
      }
    >
      {q.error ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : q.isPending ? (
        <LoadingState label="Working out the numbers" />
      ) : (
        <div className="grid gap-5">
          <p className="text-xs text-muted-foreground">
            {shortDay(q.data.from)} to {shortDay(new Date(Date.parse(q.data.to) - 1).toISOString())}, compared with {shortDay(q.data.previousFrom)} onwards.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
            {q.data.kpis.map((k) => (
              <button
                key={k.key}
                type="button"
                onClick={() => setDrill(drill === k.key ? null : k.key)}
                aria-expanded={drill === k.key}
                className={cn('grid gap-1 rounded-xl border bg-card p-4 text-left shadow-sm transition-colors', drill === k.key ? 'border-brand' : 'border-border hover:border-brand/50')}
              >
                <span className="text-xs font-medium text-muted-foreground">{k.label}</span>
                <span className="num text-3xl font-semibold">{k.count}</span>
                {k.value ? <span className="num text-sm font-medium">{money(k.value)}</span> : null}
                <Change now={k.count} before={k.previous} />
                {k.note ? <span className="text-tiny text-text-muted">{k.note}</span> : null}
              </button>
            ))}
          </div>

          {drill ? (
            <Drill title={q.data.kpis.find((k) => k.key === drill)?.label ?? ''} items={q.data.kpis.find((k) => k.key === drill)?.items ?? []} onOpen={open} onClose={() => setDrill(null)} />
          ) : null}

          <div className="grid gap-5 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <section className="rounded-xl border border-border bg-card shadow-sm">
              <h2 className="border-b border-border-soft px-4 py-2.5 text-sm font-semibold">By owner, in this period</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 font-medium">Owner</th>
                      <th className="px-4 py-2 text-right font-medium">Accounts</th>
                      <th className="px-4 py-2 text-right font-medium">Open deals</th>
                      <th className="px-4 py-2 text-right font-medium">Demos done</th>
                      <th className="px-4 py-2 text-right font-medium">Pilots started</th>
                      <th className="px-4 py-2 text-right font-medium">Won</th>
                      <th className="px-4 py-2 text-right font-medium">Won value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-soft">
                    {q.data.byOwner.map((o) => (
                      <tr key={o.ownerId}>
                        <td className="px-4 py-2 font-medium">{o.name}</td>
                        <td className="num px-4 py-2 text-right">{o.accounts}</td>
                        <td className="num px-4 py-2 text-right">{o.openDeals}</td>
                        <td className="num px-4 py-2 text-right">{o.demos}</td>
                        <td className="num px-4 py-2 text-right">{o.pilots}</td>
                        <td className="num px-4 py-2 text-right">{o.won}</td>
                        <td className="num px-4 py-2 text-right">{o.wonValue ? money(o.wonValue) : '—'}</td>
                      </tr>
                    ))}
                    {!q.data.byOwner.length ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-4 text-center text-muted-foreground">
                          No owned deals yet.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-xl border border-border bg-card shadow-sm">
              <h2 className="border-b border-border-soft px-4 py-2.5 text-sm font-semibold">Needs someone now</h2>
              <ul className="divide-y divide-border-soft">
                {q.data.attention.map((a) => (
                  <li key={a.key}>
                    <details className="group">
                      <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-2.5 text-sm hover:bg-accent/40">
                        <span className={cn('num grid min-w-7 place-items-center rounded-full px-1.5 text-xs font-semibold', a.items.length ? 'bg-warning-soft text-warning' : 'bg-muted text-muted-foreground')}>{a.items.length}</span>
                        {a.label}
                      </summary>
                      {a.items.length ? (
                        <ul className="pb-2">
                          {a.items.map((x, i) => (
                            <Row key={`${x.dealId}:${i}`} x={x} onOpen={open} ws={workspaceSlug} />
                          ))}
                        </ul>
                      ) : (
                        <p className="px-4 pb-3 text-xs text-muted-foreground">Nothing here.</p>
                      )}
                    </details>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      )}
      <DealSheet id={params.get('deal')} onClose={() => set('deal', null)} onOpen={open} />
    </Crm2Frame>
  )
}

function Row({ x, onOpen, ws }: { x: DashItem; onOpen: (id: string) => void; ws: string }) {
  return (
    <li className="flex items-center gap-2 px-4 py-1.5 text-sm">
      <Link to={paths.crm2Account(ws, x.accountId)} className={LINK_CHIP}>
        {x.accountName}
      </Link>
      {x.dealId ? (
        <button type="button" onClick={() => onOpen(x.dealId)} className={LINK_CHIP}>
          {x.label}
        </button>
      ) : (
        <span className="truncate text-xs text-muted-foreground">{x.label}</span>
      )}
      <span className="ml-auto shrink-0 text-xs text-muted-foreground">{shortDay(x.at)}</span>
    </li>
  )
}

function Drill({ title, items, onOpen, onClose }: { title: string; items: DashItem[]; onOpen: (id: string) => void; onClose: () => void }) {
  const { workspaceSlug = '' } = useParams()
  return (
    <section className="rounded-xl border border-brand bg-card shadow-sm">
      <div className="flex items-center border-b border-border-soft px-4 py-2.5">
        <h2 className="text-sm font-semibold">
          {title} <span className="font-normal text-muted-foreground">{items.length}</span>
        </h2>
        <button type="button" onClick={onClose} className={cn(LINK_CHIP, 'ml-auto text-muted-foreground')}>
          Close
        </button>
      </div>
      {items.length ? (
        <ul className="max-h-80 overflow-y-auto py-1">
          {items.map((x, i) => (
            <Row key={`${x.dealId}:${i}`} x={x} onOpen={onOpen} ws={workspaceSlug} />
          ))}
        </ul>
      ) : (
        <p className="px-4 py-3 text-sm text-muted-foreground">None in this period.</p>
      )}
    </section>
  )
}
